import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  IconButton
} from '@mui/material';
import {
  ArrowBack,
  Schedule,
  CheckCircle,
  Error,
  CloudUpload,
  Email,
  CheckBox,
  Edit,
  Save,
  Cancel,
  Refresh,
  Download,
  Share
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import * as meetingService from '../../services/meetingService';
import { useAuth } from '../../contexts/AuthContext';
import { normalizeMeetingResult, formatTimestamp } from '../../domain/meetingResult';
import { buildMeetingEmail } from '../../utils/meetingEmailFormatter';
import ShareDialog from './ShareDialog';
import EmailDialog from './EmailDialog';
import MeetingAnalysis from './MeetingAnalysis';
import TranscriptViewer from './TranscriptViewer';
import SpeakerDiagnostics from './SpeakerDiagnostics';
import ArtifactDownloads from './ArtifactDownloads';
import LegacyTranscript from './LegacyTranscript';

const MeetingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Edit states (legacy fields only; structured turns stay read-only)
  const [editingSummary, setEditingSummary] = useState(false);
  const [editingActionItems, setEditingActionItems] = useState(false);
  const [editingTranscript, setEditingTranscript] = useState(false);
  const [summaryValue, setSummaryValue] = useState('');
  const [actionItemsValue, setActionItemsValue] = useState('');
  const [transcriptValue, setTranscriptValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Reprocess / resubmit / transcribe state
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessError, setReprocessError] = useState('');
  const [resubmitting, setResubmitting] = useState(false);
  const [resubmitError, setResubmitError] = useState('');
  const [transcribing, setTranscribing] = useState(false);
  const [transcribeError, setTranscribeError] = useState('');

  // Download state
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  // Share / email state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  const loadMeeting = useCallback(async () => {
    try {
      const response = await meetingService.getMeeting(id);
      setMeeting(response.data);
      setError('');
    } catch (error) {
      setError('Failed to load meeting details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadMeeting();

    // Auto-refresh every 5 seconds if still processing
    const interval = setInterval(() => {
      if (meeting?.status === 'processing') {
        loadMeeting();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [loadMeeting, meeting?.status]);

  useEffect(() => {
    if (meeting?.title) {
      document.title = `${meeting.title} - EchoNote`;
    } else {
      document.title = 'Meeting Details - EchoNote';
    }
  }, [meeting?.title]);

  const normalized = normalizeMeetingResult(meeting);

  const getStatusChip = (status) => {
    const statusConfig = {
      uploaded: { color: 'default', icon: <CloudUpload />, label: 'Uploaded' },
      processing: { color: 'warning', icon: <Schedule />, label: 'Processing' },
      completed: { color: 'success', icon: <CheckCircle />, label: 'Completed' },
      failed: { color: 'error', icon: <Error />, label: 'Failed' }
    };

    const config = statusConfig[status] || statusConfig.uploaded;
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
      />
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleEditSummary = () => {
    setSummaryValue(meeting.summary || '');
    setEditingSummary(true);
    setSaveError('');
  };

  const handleEditActionItems = () => {
    let actionItemsText = '';
    if (meeting.action_items) {
      try {
        const parsed = typeof meeting.action_items === 'string'
          ? JSON.parse(meeting.action_items)
          : meeting.action_items;
        if (Array.isArray(parsed)) {
          actionItemsText = parsed.join('\n');
        } else {
          actionItemsText = meeting.action_items;
        }
      } catch (e) {
        actionItemsText = meeting.action_items;
      }
    }
    setActionItemsValue(actionItemsText);
    setEditingActionItems(true);
    setSaveError('');
  };

  const handleEditTranscript = () => {
    setTranscriptValue(meeting.transcript || meeting.transcription || '');
    setEditingTranscript(true);
    setSaveError('');
  };

  const handleSaveSummary = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const response = await meetingService.updateMeeting(id, { summary: summaryValue });
      setMeeting(response.data);
      setEditingSummary(false);
    } catch (error) {
      setSaveError('Failed to save summary');
      console.error('Save summary error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveActionItems = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const itemsArray = actionItemsValue.split('\n').filter(item => item.trim());
      const response = await meetingService.updateMeeting(id, {
        action_items: JSON.stringify(itemsArray)
      });
      setMeeting(response.data);
      setEditingActionItems(false);
    } catch (error) {
      setSaveError('Failed to save action items');
      console.error('Save action items error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTranscript = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const response = await meetingService.updateMeeting(id, {
        transcript: transcriptValue,
        transcription: transcriptValue
      });
      setMeeting(response.data);
      setEditingTranscript(false);
    } catch (error) {
      setSaveError('Failed to save transcript');
      console.error('Save transcript error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelSummary = () => {
    setEditingSummary(false);
    setSummaryValue('');
    setSaveError('');
  };

  const handleCancelActionItems = () => {
    setEditingActionItems(false);
    setActionItemsValue('');
    setSaveError('');
  };

  const handleCancelTranscript = () => {
    setEditingTranscript(false);
    setTranscriptValue('');
    setSaveError('');
  };

  const handleReprocess = async () => {
    setReprocessing(true);
    setReprocessError('');
    try {
      const response = await meetingService.reprocessMeeting(id);
      setMeeting(response.data);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to reprocess meeting';
      setReprocessError(errorMessage);
      console.error('Reprocess meeting error:', error);
    } finally {
      setReprocessing(false);
    }
  };

  const handleResubmit = async () => {
    setResubmitting(true);
    setResubmitError('');
    try {
      const response = await meetingService.resubmitMeeting(id);
      setMeeting(response.data);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to resubmit transcription';
      setResubmitError(errorMessage);
      console.error('Resubmit transcription error:', error);
    } finally {
      setResubmitting(false);
    }
  };

  const handleTranscribe = async () => {
    setTranscribing(true);
    setTranscribeError('');
    try {
      const response = await meetingService.transcribeMeeting(id);
      setMeeting(response.data);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to start transcription';
      setTranscribeError(errorMessage);
      console.error('Transcribe meeting error:', error);
    } finally {
      setTranscribing(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError('');
    try {
      const response = await meetingService.downloadMeetingRecording(id);
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = meeting?.originalFilename || 'recording';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to download recording';
      setDownloadError(errorMessage);
      console.error('Download recording error:', error);
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = () => {
    setShareDialogOpen(true);
  };

  const handleTimestampSeek = (seconds) => {
    console.log(`[MeetingDetail] Timestamp navigation to ${seconds}s is not wired to a media player yet.`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !meeting) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard')}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>
        <Alert severity="error">
          {error || 'Meeting not found'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>
          <Typography variant="h4" component="h1">
            Meeting Details
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          {meeting.status === 'uploaded' && (
            <Button
              variant="contained"
              color="primary"
              startIcon={transcribing ? <CircularProgress size={16} /> : <CloudUpload />}
              onClick={handleTranscribe}
              disabled={transcribing}
            >
              {transcribing ? 'Starting...' : 'Transcribe'}
            </Button>
          )}
          {user?.can_reprocess_failed_meetings && (meeting.status === 'completed' || meeting.status === 'failed') && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={resubmitting ? <CircularProgress size={16} /> : <Refresh />}
              onClick={handleResubmit}
              disabled={resubmitting}
            >
              {resubmitting ? 'Resubmitting...' : 'Resubmit Transcription'}
            </Button>
          )}
          {meeting.recordingType === 'live_recording' && meeting.filename && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={downloading ? <CircularProgress size={16} /> : <Download />}
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? 'Downloading...' : 'Download'}
            </Button>
          )}
          {meeting.status === 'completed' && (
            <>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Share />}
                onClick={handleShare}
              >
                Share
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Email />}
                onClick={() => setEmailDialogOpen(true)}
              >
                Create Email
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Meeting Info */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Meeting Information
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Title
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {meeting.title}
                </Typography>
              </Box>

              {meeting.description && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {meeting.description}
                  </Typography>
                </Box>
              )}

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                {getStatusChip(meeting.status)}
              </Box>

              {normalized.isLegacy && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Result Format
                  </Typography>
                  <Chip label="Legacy transcript" size="small" variant="outlined" />
                </Box>
              )}

              {meeting.recordingType && meeting.recordingType !== 'upload' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Recording Type
                  </Typography>
                  <Chip
                    label={meeting.recordingType === 'live_recording' ? 'Live Recording' : meeting.recordingType === 'bot_recording' ? 'Bot Recording' : 'Upload'}
                    color={meeting.recordingType === 'live_recording' ? 'error' : 'primary'}
                    size="small"
                  />
                </Box>
              )}

              {meeting.recordingDuration && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Recording Duration
                  </Typography>
                  <Typography variant="body1">
                    {formatTimestamp(meeting.recordingDuration)}
                  </Typography>
                </Box>
              )}

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Uploaded By
                </Typography>
                <Typography variant="body1">
                  {meeting.uploadedBy?.firstName} {meeting.uploadedBy?.lastName}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Upload Date
                </Typography>
                <Typography variant="body1">
                  {new Date(meeting.createdAt).toLocaleString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* File Info */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                File Information
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Original Filename
                </Typography>
                <Typography variant="body1">
                  {meeting.originalFilename}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  File Size
                </Typography>
                <Typography variant="body1">
                  {formatFileSize(meeting.fileSize)}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  File Type
                </Typography>
                <Typography variant="body1">
                  {meeting.mimeType}
                </Typography>
              </Box>

              {meeting.processingStartedAt && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Processing Started
                  </Typography>
                  <Typography variant="body1">
                    {new Date(meeting.processingStartedAt).toLocaleString()}
                  </Typography>
                </Box>
              )}

              {meeting.processingCompletedAt && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Processing Completed
                  </Typography>
                  <Typography variant="body1">
                    {new Date(meeting.processingCompletedAt).toLocaleString()}
                  </Typography>
                </Box>
              )}

              {meeting.processed_folder && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Folder Location
                  </Typography>
                  <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                    {meeting.processed_folder}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Resubmit Error Message */}
        {resubmitError && (
          <Grid item xs={12}>
            <Alert severity="error" onClose={() => setResubmitError('')}>
              {resubmitError}
            </Alert>
          </Grid>
        )}

        {/* Transcribe Error Message */}
        {transcribeError && (
          <Grid item xs={12}>
            <Alert severity="error" onClose={() => setTranscribeError('')}>
              {transcribeError}
            </Alert>
          </Grid>
        )}

        {/* Download Error Message */}
        {downloadError && (
          <Grid item xs={12}>
            <Alert severity="error" onClose={() => setDownloadError('')}>
              {downloadError}
            </Alert>
          </Grid>
        )}

        {/* Error Message */}
        {meeting.status === 'failed' && (
          <Grid item xs={12}>
            <Alert
              severity="error"
              action={
                user?.can_reprocess_failed_meetings && (
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={reprocessing ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                    onClick={handleReprocess}
                    disabled={reprocessing}
                  >
                    {reprocessing ? 'Reprocessing...' : 'Reprocess'}
                  </Button>
                )
              }
            >
              <Typography variant="body1" fontWeight="bold">
                Processing Failed
              </Typography>
              {meeting.errorMessage}
            </Alert>
            {reprocessError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {reprocessError}
              </Alert>
            )}
          </Grid>
        )}

        {/* Processing Status */}
        {meeting.status === 'processing' && (
          <Grid item xs={12}>
            <Alert severity="info">
              <Box display="flex" alignItems="center">
                <CircularProgress size={20} sx={{ mr: 2 }} />
                <Typography>
                  Your meeting is being processed. This page will update automatically when complete.
                </Typography>
              </Box>
            </Alert>
          </Grid>
        )}

        {/* Transcription Results */}
        {meeting.status === 'completed' && (
          <>
            {normalized.isLegacy ? (
              <>
                {/* Legacy summary */}
                {meeting.summary && (
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <Typography variant="h6">Summary</Typography>
                          {!editingSummary && (
                            <IconButton color="primary" size="small" onClick={handleEditSummary} title="Edit Summary">
                              <Edit />
                            </IconButton>
                          )}
                        </Box>
                        {editingSummary ? (
                          <Box>
                            <TextField
                              fullWidth
                              multiline
                              rows={6}
                              value={summaryValue}
                              onChange={(e) => setSummaryValue(e.target.value)}
                              variant="outlined"
                              disabled={saving}
                            />
                            <Box mt={2} display="flex" gap={1}>
                              <Button variant="contained" color="primary" startIcon={<Save />} onClick={handleSaveSummary} disabled={saving}>
                                Save
                              </Button>
                              <Button variant="outlined" startIcon={<Cancel />} onClick={handleCancelSummary} disabled={saving}>
                                Cancel
                              </Button>
                            </Box>
                            {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>}
                          </Box>
                        ) : (
                          <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                            <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                              {meeting.summary}
                            </Typography>
                          </Paper>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Legacy action items (normalized + deduplicated) */}
                {normalized.actionItems.length > 0 && (
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <Typography variant="h6">Action Items</Typography>
                          {!editingActionItems && (
                            <IconButton color="primary" size="small" onClick={handleEditActionItems} title="Edit Action Items">
                              <Edit />
                            </IconButton>
                          )}
                        </Box>
                        {editingActionItems ? (
                          <Box>
                            <TextField
                              fullWidth
                              multiline
                              rows={8}
                              value={actionItemsValue}
                              onChange={(e) => setActionItemsValue(e.target.value)}
                              variant="outlined"
                              disabled={saving}
                              helperText="Enter one action item per line"
                            />
                            <Box mt={2} display="flex" gap={1}>
                              <Button variant="contained" color="primary" startIcon={<Save />} onClick={handleSaveActionItems} disabled={saving}>
                                Save
                              </Button>
                              <Button variant="outlined" startIcon={<Cancel />} onClick={handleCancelActionItems} disabled={saving}>
                                Cancel
                              </Button>
                            </Box>
                            {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>}
                          </Box>
                        ) : (
                          <List>
                            {normalized.actionItems.map((item, index) => (
                              <ListItem key={index} divider={index < normalized.actionItems.length - 1}>
                                <ListItemIcon>
                                  <CheckBox />
                                </ListItemIcon>
                                <ListItemText primary={item} />
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Legacy transcript */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Transcription</Typography>
                        {!editingTranscript && (
                          <IconButton color="primary" size="small" onClick={handleEditTranscript} title="Edit Transcription">
                            <Edit />
                          </IconButton>
                        )}
                      </Box>
                      {editingTranscript ? (
                        <Box>
                          <TextField
                            fullWidth
                            multiline
                            rows={15}
                            value={transcriptValue}
                            onChange={(e) => setTranscriptValue(e.target.value)}
                            variant="outlined"
                            disabled={saving}
                          />
                          <Box mt={2} display="flex" gap={1}>
                            <Button variant="contained" color="primary" startIcon={<Save />} onClick={handleSaveTranscript} disabled={saving}>
                              Save
                            </Button>
                            <Button variant="outlined" startIcon={<Cancel />} onClick={handleCancelTranscript} disabled={saving}>
                              Cancel
                            </Button>
                          </Box>
                          {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>}
                        </Box>
                      ) : (
                        <LegacyTranscript text={normalized.effectiveTranscript} />
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Legacy metadata */}
                {meeting.metadata && (
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Metadata
                        </Typography>
                        <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                          {Object.entries(meeting.metadata).map(([key, value]) => (
                            <Box key={key} sx={{ mb: 1 }}>
                              <Typography variant="body2" component="span" fontWeight="bold">
                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:{' '}
                              </Typography>
                              <Typography variant="body2" component="span">
                                {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                              </Typography>
                            </Box>
                          ))}
                        </Paper>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </>
            ) : (
              <>
                {/* Structured analysis */}
                <Grid item xs={12}>
                  <MeetingAnalysis analysis={normalized.analysis} />
                </Grid>

                {/* Structured transcript viewer */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Transcription
                      </Typography>
                      <TranscriptViewer turns={normalized.turns} onSeek={handleTimestampSeek} />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Speaker diagnostics (collapsed) */}
                {normalized.diagnostics.length > 0 && (
                  <Grid item xs={12}>
                    <SpeakerDiagnostics diagnostics={normalized.diagnostics} anchors={normalized.anchors} />
                  </Grid>
                )}

                {/* Artifact downloads */}
                {normalized.artifacts.length > 0 && (
                  <Grid item xs={12}>
                    <ArtifactDownloads meetingId={meeting.id} artifacts={normalized.artifacts} />
                  </Grid>
                )}
              </>
            )}
          </>
        )}
      </Grid>

      {/* Share Dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        meetingId={id}
        meetingTitle={meeting?.title}
      />

      {/* Email Dialog */}
      <EmailDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        subject={`Meeting Summary: ${meeting?.title}`}
        body={emailDialogOpen ? buildMeetingEmail(meeting, { includeTranscript: true }) : ''}
        bodyWithoutTranscript={emailDialogOpen ? buildMeetingEmail(meeting, { includeTranscript: false }) : ''}
      />
    </Box>
  );
};

export default MeetingDetail;